import { PaymentPlanTagging } from '@lib/web-shared';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaymentAmountSelectionForm from '../components/PaymentAmountSelectionForm';
import { Pages } from '../shared/pages';
import { useGlobalState } from '../shared/state';
import { ErrorType, IInitializePaymentRequest } from '../shared/types';
import { UtagHelper } from '../utils/utagHelper';
import { sources } from '../api/sources';
import Spinner from '../components/Spinner';

const PaymentAmountSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const [policyPaymentInfo] = useGlobalState('policyPaymentInfo');
  const [, setAmountChosen] = useGlobalState('amountChosen');
  const [, setPaymentPlan] = useGlobalState('paymentPlan');
  const [clubTag] = useGlobalState('clubTag');
  const [appFlow] = useGlobalState('appFlow');
  const [isMobileApp] = useGlobalState('isMobileApp');
  const [policyMetaData] = useGlobalState('policyMetaData');
  const [selectedAutoPay] = useGlobalState('selectedAutoPay') || false;
  const [policyNumber] = useGlobalState('policyNumber');
  const [clubCode] = useGlobalState('clubCode');
  const [brokerRegion] = useGlobalState('brokerRegion');
  const { minimumDue, fullBalance, allowAutoPay, autoPayFeatureIsOffline } = policyPaymentInfo || {};
  const [tagging, setIsTagging] = useState(true);
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    const ins_min_due = minimumDue ? 'true' : 'false';
    const ins_autopaycampaign = allowAutoPay ? 'autopay required' : 'autopay not required';
    const ins_policy = policyMetaData.prefix;
    const tags = {
      ...UtagHelper.loadCommonTags(appFlow),
      page: UtagHelper.getPages(appFlow).paymentAmountSelectionPage,
    };
    UtagHelper.tag({
      ...tags,
      club: clubTag,
      ins_min_due,
      ins_autopaycampaign,
      state: policyMetaData.state,
      ins_policy,
    });
    setIsTagging(false);
  }, []);

  useEffect(() => {
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        window.location.reload();
      }
    });

    return () => {
      window.removeEventListener('pageshow', () => {});
    };
  }, []);

  const handleSubmit = (selectedAmount: number) => {
    setAmountChosen(Number(selectedAmount));
    if (Number(selectedAmount) === minimumDue) {
      setPaymentPlan(PaymentPlanTagging.Minimum);
    } else if (Number(selectedAmount) === fullBalance) {
      setPaymentPlan(PaymentPlanTagging.Full);
    } else {
      setPaymentPlan(PaymentPlanTagging.Other);
    }
    const { allowAutoPay } = policyPaymentInfo || { allowAutoPay: false };

    if (allowAutoPay && !autoPayFeatureIsOffline) {
      navigate(Pages.AutoPay);
    } else {
      submitManifest(Number(selectedAmount));
    }
  };

  const submitManifest = async (selectedAmount: number) => {
    try {
      const autoPay = !!allowAutoPay && selectedAutoPay;
      let initializePaymentRequest: IInitializePaymentRequest = {
        appFlow: appFlow!,
        amountPaid: selectedAmount || 0,
        policyNumber,
        clubCode,
        clubTag,
        autoPay: allowAutoPay ? autoPay : undefined,
        allowAutoPay: !!allowAutoPay,
        brokerRegion,
      };

      setLoading(true);
      const { manifestId } = await sources.initializePayment(initializePaymentRequest);
      let url = `https://${window.location.hostname}/payments/${manifestId}`;

      if (isMobileApp) {
        url += '?mobileapp=true';
      }
      window.location.assign(url);
    } catch (e: unknown) {
      navigate(Pages.SorryPage, {
        state: { errorTag: UtagHelper.Errors.paymentError, errorType: ErrorType.SYSTEM_ERROR },
      });
    }
  };

  if (tagging) {
    return <></>;
  }

  return isLoading ? (
    <Spinner />
  ) : (
    <PaymentAmountSelectionForm
      minimumDue={minimumDue!}
      fullBalance={fullBalance!}
      onSubmit={handleSubmit}
      appFlow={appFlow!}
      isMobileApp={isMobileApp}
    />
  );
};

export default PaymentAmountSelectionPage;
